import { useEffect, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { GlobalState } from '@/stores/reducer'
import { useMicrophonePermission } from './useMicrophonePermission'
import { useRecordingState } from './useRecordingState'
import { useRecordingEngine } from '@/features/yakureki/recording/useRecordingEngine'
import { useMyPharmacyQuery } from './queries/useMyPharmacyQuery'
import { usePostRecordingSessionMutation } from './queries/useRecordingSessionMutations'

interface UseGroupRecordAudioReturn {
  isRecording: boolean
  audioURL: string | null
  isSending: boolean
  permissionStatus: string
  /**
   * 録音開始。
   * - batch 経路: 引数は無視される (単純に MediaRecorder を start するだけ)
   * - streaming 経路: `yakurekiIds` が必須 (stream/start に渡す)
   *   省略した場合は batch にフォールバック
   * - `hasCompanionOrSubstituteMap`: event ごとの「対象患者の家族 (同伴/代理) 有無」
   *   (yakureki-back log/145/work-log/01 — 振り分け規則テーブルに反映)。
   *   batch 経路では現状無視 (Phase 2 では streaming 経路のみ対応)。
   */
  startRecording: (params?: {
    yakurekiIds?: string[]
    memoItems?: string[]
    hasCompanionOrSubstituteMap?: Record<string, boolean>
  }) => Promise<void>
  stopRecording: () => void
  cancelRecording: () => void
  clearAudio: () => void
  /**
   * 録音停止後の送信。
   * - batch 経路: 収集した audioURL を postRecordingSessionAudio に送る
   * - streaming 経路: streaming 側 finalize で audio multipart 同梱済のため呼び出し不要 (no-op)。
   *   ただし fallback 経由で audioURL が存在する場合は batch 経路に流す
   */
  sendGroupAudio: (yakurekiIds: string[], memoItems?: string[]) => Promise<void>
}

/**
 * まとめて録音フック (streaming / batch 自動切替)
 *
 * transcription_mode=streaming の薬局では、`startRecording` 時点で yakurekiIds を渡せば
 * streaming 経路を使う。fallback 時は収集した blob を既存 batch mutation に流す。
 *
 * 設計: yakureki-back/log/143/design/stt_architecture.md §7.1 / §9.1
 *
 * TODO(future): useRecordAudio と同様、hook 物理分離 + Context dispatcher 化 (C' 方針)。
 * 詳細: log/93_20260424_streaming_ui_foundation/design/namespace_and_overlay.md §4.1
 */
export const useGroupRecordAudio = (
  onComplete?: () => void,
  pharmacistId?: string
): UseGroupRecordAudioReturn => {
  const isLoggedIn = useSelector((state: GlobalState) => state.isLoggedIn)
  const [isSending, setIsSending] = useState(false)
  // streaming fallback の Blob URL を recording state とは別管理する.
  // clearAudio / sendGroupAudio の deps で参照するため hook body 上部で宣言.
  const [fallbackAudioURL, setFallbackAudioURL] = useState<string | null>(null)

  const recordingSessionMutation = usePostRecordingSessionMutation()
  const { transcriptionMode } = useMyPharmacyQuery()
  const isStreamingMode = transcriptionMode === 'streaming'

  const { permissionStatus, mediaRecorder, requestMicrophoneAccess } =
    useMicrophonePermission(isLoggedIn, { autoRequest: false })

  const recording = useRecordingState(
    mediaRecorder,
    permissionStatus,
    requestMicrophoneAccess
  )

  const streaming = useRecordingEngine()

  // streaming が active かどうか
  const isStreamingActive =
    streaming.state === 'starting' ||
    streaming.state === 'recording' ||
    streaming.state === 'fallback'
  const isStreamingFinalizing = streaming.state === 'finalizing'

  const effectiveIsRecording = recording.isRecording || isStreamingActive
  const effectiveIsSending = isSending || isStreamingFinalizing

  // startRecording: streaming 優先、yakurekiIds 未指定 or batch モードなら既存経路
  const startRecording = useCallback(
    async (params?: {
      yakurekiIds?: string[]
      memoItems?: string[]
      hasCompanionOrSubstituteMap?: Record<string, boolean>
    }) => {
      if (
        isStreamingMode &&
        params?.yakurekiIds &&
        params.yakurekiIds.length > 0
      ) {
        console.info(
          '[transcription-dispatch] useGroupRecordAudio.start: streaming path',
          {
            yakurekiIds: params.yakurekiIds,
            transcriptionMode,
            hasCompanionOrSubstituteMap: params.hasCompanionOrSubstituteMap,
          }
        )
        await streaming.start({
          yakurekiIds: params.yakurekiIds,
          pharmacistId,
          memoItems: params.memoItems,
          hasCompanionOrSubstituteMap: params.hasCompanionOrSubstituteMap,
        })
        return
      }
      console.info(
        '[transcription-dispatch] useGroupRecordAudio.start: legacy batch path',
        {
          yakurekiIds: params?.yakurekiIds,
          isStreamingMode,
          transcriptionMode,
        }
      )
      await recording.startRecording()
    },
    [isStreamingMode, streaming, recording, pharmacistId, transcriptionMode]
  )

  const stopRecording = useCallback(() => {
    if (isStreamingActive) {
      streaming.stop()
      return
    }
    recording.stopRecording()
  }, [isStreamingActive, streaming, recording])

  const cancelRecording = useCallback(() => {
    if (isStreamingActive || streaming.state === 'finalizing') {
      // cancel() は backend 通知を await できる promise を返すが、 まとめて録音は
      // 破棄後に自 page の cache を読み直さないので待たない (no-floating-promises 対策).
      void streaming.cancel()
      return
    }
    recording.cancelRecording()
  }, [isStreamingActive, streaming, recording])

  const clearAudio = useCallback(() => {
    if (streaming.state === 'completed' || streaming.state === 'failed') {
      // 完了/失敗後の cleanup. backend に通知すると finalize 済 SS が誤って
      // CANCELLED 上書きされるため reset (local-only) を使う.
      streaming.reset()
    }
    // streaming fallback 経路で createObjectURL した Blob URL は streaming.reset()
    // では解放されないため、ここで明示的に revoke + state クリアする.
    // (これを忘れると stale な fallbackAudioURL が effectiveAudioURL に残り、
    // 「録り直し」後も古い録音 preview が見えてしまう.)
    if (fallbackAudioURL) {
      URL.revokeObjectURL(fallbackAudioURL)
      setFallbackAudioURL(null)
    }
    recording.clearAudio()
  }, [streaming, recording, fallbackAudioURL])

  // streaming 完了を検知: fallbackBlob があれば batch mutation へ、なければ onComplete 呼ぶ
  useEffect(() => {
    if (streaming.state !== 'completed') return

    const blob = streaming.fallbackBlob
    if (!blob) {
      // streaming 正常完了: backend で finalize に audio 同梱済、即 onComplete
      onComplete?.()
      streaming.reset()
      return
    }

    // fallback: blob を既存 batch mutation に流す (yakurekiIds は再度 send 側から渡される想定)
    // ただしここでは ids の再取得ができないため、caller が sendGroupAudio を呼ぶまで待つ
    // → audioURL を生成して recording state に格納することで、caller 側の既存 UX に寄せる
    const url = URL.createObjectURL(blob)
    // useRecordingState 内部の setAudioURL を直接触れないため、
    // caller は streaming.fallbackBlob を参照するか sendGroupAudio 時に blob が使われる
    // 本フックでは blob を state に保持し、sendGroupAudio 時に優先使用する
    setFallbackAudioURL(url)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streaming.state, streaming.fallbackBlob])

  const effectiveAudioURL = recording.audioURL ?? fallbackAudioURL

  const sendGroupAudio = useCallback(
    async (yakurekiIds: string[], memoItems?: string[]) => {
      // streaming 正常完了: onComplete は useEffect で既に呼ばれているため no-op
      if (
        streaming.state === 'completed' &&
        !fallbackAudioURL &&
        !recording.audioURL
      ) {
        return
      }

      // recording.audioURL を優先. 無ければ streaming fallback の Blob URL を使う.
      // どちらの経路かでクリーンアップ手順が異なるため flag で覚えておく.
      const isFallbackSource = !recording.audioURL && !!fallbackAudioURL
      const source = recording.audioURL ?? fallbackAudioURL
      if (!source || isSending) return

      setIsSending(true)
      try {
        await recordingSessionMutation.mutateAsync({
          audioSource: source,
          yakurekiIds,
          pharmacistId,
          memoItems,
        })
        // success: ソースに応じた cleanup. 二重 revoke を避けるため finally では
        // 触らない (recording.clearAudio() は内部で recording.audioURL を revoke 済).
        if (isFallbackSource) {
          URL.revokeObjectURL(source)
          setFallbackAudioURL(null)
          // fallback 経路完了後の cleanup (backend に SS は無いか既に terminal)
          streaming.reset()
        } else {
          recording.clearAudio()
        }
        onComplete?.()
      } catch {
        // mutation 側で toast 表示済み. ここでは URL を revoke せず state も保持し、
        // ユーザーが「録り直し」or 同じ blob で再送できるようにする.
        // (旧実装は finally で URL.revokeObjectURL(source) を呼んでおり、state は
        //  URL 文字列を保持したまま実体だけ revoke されてリトライが silent fail
        //  していた.)
      } finally {
        setIsSending(false)
      }
    },
    [
      recording,
      fallbackAudioURL,
      isSending,
      recordingSessionMutation,
      onComplete,
      pharmacistId,
      streaming,
    ]
  )

  return {
    isRecording: effectiveIsRecording,
    audioURL: effectiveAudioURL,
    isSending: effectiveIsSending,
    permissionStatus,
    startRecording,
    stopRecording,
    cancelRecording,
    clearAudio,
    sendGroupAudio,
  }
}
